import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { setDataToDownload } from "store/slices/energyPerformance/energyPerformanceSliceNew";

import { chartTypesEnums, diagramTypes, valuesEnums } from "../enums/dynamicDashboard";
import { distinctFilter, keepKeysFromObjects } from "./dataManipulation";
import {
  echartsDefaultTooltipFormatter,
  getAxisNameWithUnit,
  getDataKeyFromDiagramFilterName,
  getItemColor,
  getSeriesMixedName,
  hasSecondYAxis,
} from "./dynamicDashboard";
import { formatNumberBasedOnUser } from "./namesManipulation";
import { getCSSVar } from "./styling";

export const getPieAndDoughnutOptions = ({
  allAggregatedData,
  chartsConfigs,
  chartType,
  dataPoint,
  dataStacks,
  getColor,
  getColumnDisplayName,
  getItemColor,
  height,
  hoverEffect,
  isItemHovered,
  legendsPosition,
  legendsWidth,
  partitions,
  partitionsSortFunction,
  showPieLabels,
  somethingIsHovered,
  stacks,
  width,
  xAxis,
  yAxis,
}) => {
  const noOfCharts = allAggregatedData.length;

  let availableWidth = legendsPosition === "right" ? width - legendsWidth : width;
  const xOuterRadius = availableWidth / (3 * noOfCharts + 1);
  const yOuterRadius = height / 3;
  const outerRadius = Math.min(xOuterRadius, yOuterRadius);
  const marginX = availableWidth - (3 * noOfCharts + 1) * outerRadius;

  const isDoughnut = chartType === chartTypesEnums.DOUGHNUT;
  const doughnutRadiusUnit = Math.min(availableWidth / (5 * noOfCharts + 0.25), height / (5 * noOfCharts + 0.25));

  const getValueForRepresentation = (dataPoint, stack, partition) => {
    if (stacks && partitions) {
      return `(${stack})-(${partition})`;
    } else if (stacks) {
      return stack;
    } else if (partitions) {
      return partition;
    } else {
      return dataPoint;
    }
  };

  return {
    series: allAggregatedData?.map((data, stackIndex) => {
      const stack = dataStacks[stackIndex];

      const sortedDataWithIndex = data.map((row, indexBackup) => ({
        ...row,
        indexBackup,
      }));
      sortedDataWithIndex.sort(partitionsSortFunction);
      return {
        avoidLabelOverlap: true,
        center: [
          isDoughnut
            ? `${(100 * availableWidth) / (2 * width)}%`
            : `${marginX / 2 + (3 * stackIndex + 2) * outerRadius}`,
          "50%",
        ],
        color: sortedDataWithIndex?.map((row, partitionIndex) => {
          let partition, valueForRepresentation;
          partition = row[partitions];
          valueForRepresentation = getValueForRepresentation(dataPoint, stack, partition);

          const color =
            getColor &&
            getColor({
              chartType,
              dataPoint,
              diagrams: chartsConfigs,
              quantity: valueForRepresentation,
            });
          return color
            ? color
            : stacks
            ? getItemColor(stackIndex, partitions ? partitionIndex : undefined)
            : getItemColor(partitionIndex);
        }),
        data: sortedDataWithIndex?.map((row, i) => {
          let partition, valueForRepresentation;
          partition = row[partitions];
          valueForRepresentation = getValueForRepresentation(dataPoint, stack, partition);

          return {
            itemStyle: {
              opacity:
                somethingIsHovered &&
                isItemHovered &&
                !isItemHovered({
                  chartType,
                  diagrams: chartsConfigs,
                  row: { ...row, [partitions]: partition, [stacks]: stack },
                  xAxis,
                  yAxis,
                })
                  ? 0.05
                  : 1,
            },
            name: valueForRepresentation,
            row: hoverEffect ? { ...row, [partitions]: partition, [stacks]: stack } : {},
            value: row[dataPoint],
          };
        }),
        emphasis: {
          focus: hoverEffect ? undefined : "series",
        },
        itemStyle: {
          borderColor: getCSSVar("--color-purpose-graph-pie-slice-border"),
          borderRadius: isDoughnut ? 4 : 6,
          borderWidth: 1,
        },
        label: {
          backgroundColor: showPieLabels ? getCSSVar("--color-purpose-graph-pie-label-background") : "",
          borderColor: showPieLabels ? getCSSVar("--color-purpose-graph-pie-label-border") : "",
          borderRadius: 4,
          borderWidth: 1,
          formatter: function (params) {
            const percent = params?.percent?.toFixed(2);
            const value = formatNumberBasedOnUser(params?.value);
            const name = params?.name;

            const withLabel = `{b|${name}}\n{hr|}\n{value|${value}}{space|}{per|${percent}%}`;
            const withoutLabel = `${percent}% \n  (${value})`;
            return showPieLabels ? withLabel : withoutLabel;
          },
          padding: [4, 6],
          position: showPieLabels ? "outside" : "inside",
          rich: {
            b: {
              align: "center",
            },
            hr: {
              borderColor: getCSSVar("--color-purpose-graph-pie-label-border"),
              borderWidth: 1,
              height: 0,
              lineHeight: 6,
              width: "100%",
            },
            per: {
              align: "center",
              backgroundColor: getCSSVar("--color-purpose-graph-pie-label-percent-background"),
              borderRadius: 4,
              color: getCSSVar("--color-purpose-graph-pie-label-percent-text"),
              fontSize: 13,
              padding: [3, 4],
            },
            space: {
              width: 10,
            },
            value: {
              align: "center",
              color: getCSSVar("--color-purpose-graph-pie-label-value-text"),
              fontSize: 13,
            },
          },
          show: true,
        },
        labelLine: {
          show: true,
        },
        name: partitions,
        radius: [
          isDoughnut ? (2 * stackIndex + 1) * doughnutRadiusUnit : 0,
          isDoughnut ? (2 * stackIndex + 2) * doughnutRadiusUnit : outerRadius,
        ],
        tooltip: {
          valueFormatter: echartsDefaultTooltipFormatter,
        },
        type: "pie",
      };
    }),
    title: [
      ...(dataStacks?.map((stackName, stackIndex) => ({
        left: isDoughnut ? "50%" : `${marginX / 2 + (3 * stackIndex + 2) * outerRadius}`,
        subtext: !isDoughnut && getColumnDisplayName({ colName: stackName }),
        textAlign: "center",
        top: height / 2 - outerRadius - 40,
      })) ?? []),
    ],
    tooltip: {
      trigger: "item",
    },
  };
};

export const getScatterChartOptions = ({
  config,
  diagrams,
  getColor,
  highPerformance,
  hoverEffect,
  isItemHovered,
  selectedConfigs,
  somethingIsHovered,
  sortedData,
  xDataKey,
}) => {
  const diagramsFiltersNames = Object.keys(diagrams?.[0] ?? {})?.filter((key) =>
    key.startsWith(valuesEnums.DIAGRAM_FILTER_PREFIX)
  );
  const diagramsActiveFiltersNames = diagramsFiltersNames?.filter((col) =>
    selectedConfigs?.some((conf) => conf[col] !== undefined)
  );

  const diagramsFiltersDataKeys = diagramsActiveFiltersNames?.map((filterName) =>
    getDataKeyFromDiagramFilterName({ filterName })
  );

  const option = {
    series: selectedConfigs?.map((selectedConfig, i) => {
      const performanceProps = highPerformance
        ? {
            large: true,
            largeThreshold: 1000,
            progressive: 500,
            progressiveThreshold: 1000,
          }
        : {};
      const activeFiltersNames = diagramsFiltersNames?.filter((col) => selectedConfig[col] !== undefined);
      const activeFiltersNamesAndValues = activeFiltersNames?.map((col) => [col, selectedConfig[col]]);

      const seriesMixedName = getSeriesMixedName({
        filters: activeFiltersNamesAndValues,
        seriesName: selectedConfig.dataKey,
      });
      const color =
        getColor &&
        getColor({
          chartType: config?.chartType,
          dataPoint: selectedConfig.dataKey,
          diagrams,
          quantity: seriesMixedName,
        });
      const seriesRelatedData = sortedData?.filter((row) => {
        let output = true;
        activeFiltersNames?.forEach((filterName, index) => {
          const selectedValue = selectedConfig[filterName];

          const dataColName = diagramsFiltersDataKeys[index];
          const rowValue = row[dataColName];
          if (selectedValue !== rowValue) output = false;
        });
        return output;
      });
      let seriesAggregatedData = seriesRelatedData;

      return {
        color: color ?? getItemColor(i),
        data: seriesAggregatedData?.map((row) => ({
          itemStyle: {
            opacity:
              somethingIsHovered &&
              isItemHovered &&
              !isItemHovered({
                chartType: config?.chartType,
                diagrams,
                diagramsDataKey: selectedConfig?.dataKey,
                row,
                selectedConfig,
                xAxis: config?.xAxis,
                yAxis: config?.yAxis,
              })
                ? 0.05
                : 1,
          },
          row: {
            ...(row ?? {}),
            diagramsDataKey: selectedConfig?.dataKey,
          },
          value: [row[xDataKey], row[selectedConfig?.dataKey]],
        })),
        emphasis: {
          focus: hoverEffect ? undefined : "series",
        },
        maxValue: seriesAggregatedData?.length
          ? Math.max(...seriesAggregatedData.map((row) => row[selectedConfig?.dataKey] ?? 0))
          : 0,
        name: seriesMixedName,
        tooltip: {
          trigger: "item",
          valueFormatter: echartsDefaultTooltipFormatter,
        },
        type: chartTypesEnums.SCATTER,
        yAxisIndex: selectedConfig.asSecondYAxis ? 1 : 0,
        ...performanceProps,
      };
    }),
  };
  return option;
};

export const useGetScatterChartAxisOption = ({
  config,
  getColumnDisplayName,
  getUnit,
  invertXAxis,
  invertYAxis,
  secondYAxisNameConfig,
  selectedConfigs,
  xAxisNameConfig,
  xAxisValues,
  xDataKey,
  xIsNumeric,
  yAxisNameConfig,
}) => {
  const xIsCategory = selectedConfigs?.some((selectedConfig) => selectedConfig.diagramType === diagramTypes.BAR);

  let xAxis = xIsNumeric
    ? {
        inverse: invertXAxis,
        type: xIsCategory ? "category" : "value",
      }
    : {
        axisPointer: {
          type: "shadow",
        },
        data: xAxisValues,
        inverse: invertXAxis,
      };

  const xUnit = getUnit({ config, quantity: xDataKey });
  const xAxisName =
    xAxisNameConfig ||
    getAxisNameWithUnit({
      dataKey: getColumnDisplayName({ colName: xDataKey }),
      unit: xUnit,
    });
  xAxis = [{ ...xAxis, name: xAxisName, nameGap: 25, nameLocation: "center" }];

  let yAxis = xIsNumeric
    ? [
        {
          inverse: invertYAxis,
        },
      ]
    : [
        {
          inverse: invertYAxis,
          type: "value",
        },
      ];

  const mainYAxisUnits = selectedConfigs
    ?.filter((config) => !config.asSecondYAxis)
    .map((config) =>
      getAxisNameWithUnit({
        unit: getUnit({ config, quantity: config.dataKey }),
      })
    );
  const mainYAxisUnitUnique = mainYAxisUnits.filter(distinctFilter);
  const mainYAxisUnitText = mainYAxisUnitUnique.join(" - ");

  const secondYAxisUnits = selectedConfigs
    ?.filter((config) => config.asSecondYAxis)
    .map((config) =>
      getAxisNameWithUnit({
        unit: getUnit({ config, quantity: config.dataKey }),
      })
    );
  const secondYAxisUnitUnique = secondYAxisUnits.filter(distinctFilter);
  const secondYAxisUnitText = secondYAxisUnitUnique.join(" - ");

  yAxis = [{ ...yAxis[0], name: yAxisNameConfig || mainYAxisUnitText }];

  if (hasSecondYAxis({ diagrams: config?.diagrams }))
    yAxis.push({ ...yAxis[0], name: secondYAxisNameConfig || secondYAxisUnitText });

  return { xAxis, yAxis };
};

export const useScatterChartDataDownload = ({ data, id, selectedConfigs, xDataKey }) => {
  const dispatch = useDispatch();

  const dataKeysForDownload = selectedConfigs?.map((config) => config.dataKey);

  useEffect(() => {
    try {
      if (dataKeysForDownload) {
        dataKeysForDownload?.push(xDataKey);
        let dataToDownload = keepKeysFromObjects(data, dataKeysForDownload);
        dispatch(setDataToDownload({ data: dataToDownload, id: id }));
      }
    } catch {
      //Do nothing
    }
  }, [JSON.stringify(dataKeysForDownload), data, id]);
};
